import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  IconButton, Tooltip, Popover, Typography, Box, Divider,
  Radio, Checkbox, FormControlLabel,
} from '@material-ui/core';
import LayersIcon from '@material-ui/icons/Layers';
import { GIS_OVERLAYS } from '../hooks/useMapLayers';

const useStyles = makeStyles(() => ({
  floatBtn: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 38,
    height: 38,
    background: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    zIndex: 1100,
    '&:hover': { background: '#f5f5f5' },
  },
  popover: {
    minWidth: 260,
    maxWidth: 280,
    padding: 12,
    maxHeight: 500,
    overflowY: 'auto',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 6,
    marginBottom: 6,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 0',
    gap: 4,
    cursor: 'pointer',
    borderRadius: 4,
    fontSize: 13,
    '&:hover': { background: '#f5f5f5' },
  },
  label: {
    fontSize: 13,
    flex: 1,
  },
  subList: {
    maxHeight: 180,
    overflowY: 'auto',
    paddingRight: 4,
    marginBottom: 8,
  },
  empty: {
    fontSize: 12,
    color: '#999',
    fontStyle: 'italic',
  },
}));

/**
 * Floating map layer control — sits in the top-right corner of the map.
 * Collapsed = small button with a Layers icon.
 * Expanded = popover with:
 *   - Basemap radio list (Google + Maricopa yearly aerials)
 *   - GIS overlays checkbox list (parcels, flood, topo, etc)
 *   - Data layer toggles (restricted areas, property lines, geofence)
 */
const MapLayerControl = ({
  providers,
  currentProviderName,
  onSelectBasemap,
  activeOverlays,
  onToggleOverlay,
  dataLayers,
  onToggleDataLayer,
}) => {
  const classes = useStyles();
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);

  // Split providers into Google vs yearly aerials
  const googleProviders = providers.filter(p => p.group === 'google' || p.name?.startsWith('Google'));
  const yearProviders = providers.filter(p => !(p.group === 'google' || p.name?.startsWith('Google')));

  return (
    <>
      <Tooltip title="Map layers" placement="left">
        <IconButton
          ref={btnRef}
          className={classes.floatBtn}
          onClick={() => setOpen(true)}
          size="small"
        >
          <LayersIcon style={{ fontSize: 20, color: '#555' }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={btnRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.popover }}
      >
        {/* Basemap section */}
        <Typography className={classes.sectionTitle}>Basemap</Typography>
        {googleProviders.map(p => (
          <label key={p.name} className={classes.item} htmlFor={`bm-${p.name}`}>
            <Radio
              id={`bm-${p.name}`}
              size="small"
              checked={currentProviderName === p.name}
              onChange={() => onSelectBasemap(p.name)}
              color="primary"
              style={{ padding: 4 }}
            />
            <span className={classes.label}>{p.displayName || p.name}</span>
          </label>
        ))}

        {/* Year providers — only show if some loaded */}
        {yearProviders.length > 0 && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            <Typography className={classes.sectionTitle}>Maricopa Aerials (by year)</Typography>
            <div className={classes.subList}>
              {yearProviders.map(p => (
                <label key={p.name} className={classes.item} htmlFor={`bm-${p.name}`}>
                  <Radio
                    id={`bm-${p.name}`}
                    size="small"
                    checked={currentProviderName === p.name}
                    onChange={() => onSelectBasemap(p.name)}
                    color="primary"
                    style={{ padding: 4 }}
                  />
                  <span className={classes.label}>{p.displayName || p.name}</span>
                </label>
              ))}
            </div>
          </>
        )}

        <Divider style={{ margin: '8px 0' }} />

        {/* GIS overlays section */}
        <Typography className={classes.sectionTitle}>GIS Overlays</Typography>
        {GIS_OVERLAYS.map(def => (
          <label key={def.key} className={classes.item} htmlFor={`ov-${def.key}`}>
            <Checkbox
              id={`ov-${def.key}`}
              size="small"
              checked={activeOverlays.includes(def.key)}
              onChange={() => onToggleOverlay(def.key)}
              color="primary"
              style={{ padding: 4 }}
            />
            <span className={classes.label}>{def.label}</span>
          </label>
        ))}

        <Divider style={{ margin: '8px 0' }} />

        {/* Data layer toggles */}
        <Typography className={classes.sectionTitle}>Jobsite Data</Typography>
        <label className={classes.item} htmlFor="dl-geofence">
          <Checkbox
            id="dl-geofence"
            size="small"
            checked={!!dataLayers.geofence}
            onChange={() => onToggleDataLayer('geofence')}
            color="primary"
            style={{ padding: 4 }}
          />
          <span className={classes.label}>Geofence</span>
        </label>
        <label className={classes.item} htmlFor="dl-propertyLines">
          <Checkbox
            id="dl-propertyLines"
            size="small"
            checked={!!dataLayers.propertyLines}
            onChange={() => onToggleDataLayer('propertyLines')}
            color="primary"
            style={{ padding: 4 }}
          />
          <span className={classes.label}>Property Lines</span>
        </label>
        <label className={classes.item} htmlFor="dl-restrictedAreas">
          <Checkbox
            id="dl-restrictedAreas"
            size="small"
            checked={!!dataLayers.restrictedAreas}
            onChange={() => onToggleDataLayer('restrictedAreas')}
            color="primary"
            style={{ padding: 4 }}
          />
          <span className={classes.label}>Restricted Areas</span>
        </label>
        <label className={classes.item} htmlFor="dl-serviceAreas">
          <Checkbox
            id="dl-serviceAreas"
            size="small"
            checked={dataLayers.serviceAreas !== false}
            onChange={() => onToggleDataLayer('serviceAreas')}
            color="primary"
            style={{ padding: 4 }}
          />
          <span className={classes.label}>Service Areas</span>
        </label>
        <label className={classes.item} htmlFor="dl-takeoffAreas">
          <Checkbox
            id="dl-takeoffAreas"
            size="small"
            checked={dataLayers.takeoffAreas !== false}
            onChange={() => onToggleDataLayer('takeoffAreas')}
            color="primary"
            style={{ padding: 4 }}
          />
          <span className={classes.label}>Takeoff Areas</span>
        </label>
        <label className={classes.item} htmlFor="dl-nearbyAreas">
          <Checkbox
            id="dl-nearbyAreas"
            size="small"
            checked={dataLayers.nearbyAreas !== false}
            onChange={() => onToggleDataLayer('nearbyAreas')}
            color="primary"
            style={{ padding: 4 }}
          />
          <span className={classes.label}>Nearby Customer Areas</span>
        </label>
        <label className={classes.item} htmlFor="dl-measurements">
          <Checkbox
            id="dl-measurements"
            size="small"
            checked={!!dataLayers.measurements}
            onChange={() => onToggleDataLayer('measurements')}
            color="primary"
            style={{ padding: 4 }}
          />
          <span className={classes.label}>Measurements (SF/LF)</span>
        </label>
      </Popover>
    </>
  );
};

export default MapLayerControl;
